/**
 * Client-side image prep before upload.
 * Every marketplace / catalog thumbnail is forced to a square web size
 * so multi-MB AI dumps never go to Cloudinary as-is.
 *
 * Mitra dashboard feature cards use a separate 3:2 landscape size
 * (not catalog 512×512) — see MITRA_CARD_* constants.
 */

export const CATALOG_THUMB_PX = 512;
export const CATALOG_THUMB_TARGET_BYTES = 120 * 1024; // ~KB web thumb
export const CATALOG_THUMB_MAX_INPUT_BYTES = 12 * 1024 * 1024;

/** Locsy Mitra Partner / Operations feature card art (matches bundled webp). */
export const MITRA_CARD_WIDTH = 1024;
export const MITRA_CARD_HEIGHT = 682;
export const MITRA_CARD_ASPECT = '3:2';
export const MITRA_CARD_TARGET_BYTES = 180 * 1024;
export const MITRA_CARD_MAX_INPUT_BYTES = 12 * 1024 * 1024;

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image file'));
    };
    img.src = url;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => {
    if (quality == null) {
      canvas.toBlob(resolve, type);
    } else {
      canvas.toBlob(resolve, type, quality);
    }
  });

/**
 * Resize + compress any image File to a square catalog thumbnail.
 * @param {File} file
 * @param {{ size?: number, targetBytes?: number, keepTransparency?: boolean }} [opts]
 * @returns {Promise<File>}
 */
export async function compressImageForUpload(file, opts = {}) {
  if (!file || !String(file.type || '').startsWith('image/')) {
    throw new Error('Please choose an image file');
  }

  const size = opts.size || CATALOG_THUMB_PX;
  const targetBytes = opts.targetBytes || CATALOG_THUMB_TARGET_BYTES;
  const keepTransparency = opts.keepTransparency !== false;

  const img = await loadImageFromFile(file);
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  if (!srcW || !srcH) throw new Error('Invalid image dimensions');

  const side = Math.min(srcW, srcH);
  const sx = Math.floor((srcW - side) / 2);
  const sy = Math.floor((srcH - side) / 2);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!keepTransparency) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
  } else {
    ctx.clearRect(0, 0, size, size);
  }
  ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);

  const baseName =
    String(file.name || 'thumbnail')
      .replace(/\.[^.]+$/, '')
      .replace(/[^\w.-]+/g, '_')
      .slice(0, 80) || 'thumbnail';

  if (keepTransparency) {
    const pngBlob = await canvasToBlob(canvas, 'image/png');
    if (pngBlob && pngBlob.size <= targetBytes) {
      return new File([pngBlob], `${baseName}_${size}.png`, {
        type: 'image/png',
        lastModified: Date.now(),
      });
    }
  }

  let quality = 0.82;
  let webpBlob = null;
  while (quality >= 0.5) {
    // eslint-disable-next-line no-await-in-loop
    webpBlob = await canvasToBlob(canvas, 'image/webp', quality);
    if (webpBlob && webpBlob.size <= targetBytes) break;
    quality -= 0.08;
  }

  if (webpBlob) {
    return new File([webpBlob], `${baseName}_${size}.webp`, {
      type: 'image/webp',
      lastModified: Date.now(),
    });
  }

  const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', 0.78);
  if (!jpegBlob) throw new Error('Could not compress image');
  return new File([jpegBlob], `${baseName}_${size}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/** Cloudinary delivery transform so served URL is also capped at thumb size. */
export function toOptimizedThumbDeliveryUrl(url, px = CATALOG_THUMB_PX) {
  if (!url || typeof url !== 'string' || !url.includes('/upload/')) return url;
  if (url.includes(`w_${px},h_${px}`)) return url;
  return url.replace(
    '/upload/',
    `/upload/w_${px},h_${px},c_fill,f_auto,q_auto:eco/`,
  );
}

/** Landscape Cloudinary delivery for Mitra dashboard cards (3:2). */
export function toOptimizedMitraCardDeliveryUrl(
  url,
  width = MITRA_CARD_WIDTH,
  height = MITRA_CARD_HEIGHT,
) {
  if (!url || typeof url !== 'string' || !url.includes('/upload/')) return url;
  if (url.includes(`w_${width},h_${height}`)) return url;
  return url.replace(
    '/upload/',
    `/upload/w_${width},h_${height},c_fill,f_auto,q_auto:eco/`,
  );
}

/**
 * Center-crop to 3:2 and resize for Mitra feature cards (NOT square catalog thumbs).
 * @param {File} file
 * @param {{ width?: number, height?: number, targetBytes?: number }} [opts]
 * @returns {Promise<File>}
 */
export async function compressMitraCardImageForUpload(file, opts = {}) {
  if (!file || !String(file.type || '').startsWith('image/')) {
    throw new Error('Please choose an image file');
  }

  const outW = opts.width || MITRA_CARD_WIDTH;
  const outH = opts.height || MITRA_CARD_HEIGHT;
  const targetBytes = opts.targetBytes || MITRA_CARD_TARGET_BYTES;
  const targetAspect = outW / outH;

  const img = await loadImageFromFile(file);
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  if (!srcW || !srcH) throw new Error('Invalid image dimensions');

  let cropW = srcW;
  let cropH = Math.round(srcW / targetAspect);
  if (cropH > srcH) {
    cropH = srcH;
    cropW = Math.round(srcH * targetAspect);
  }
  const sx = Math.floor((srcW - cropW) / 2);
  const sy = Math.floor((srcH - cropH) / 2);

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#0F172A';
  ctx.fillRect(0, 0, outW, outH);
  ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, outW, outH);

  const baseName =
    String(file.name || 'mitra_card')
      .replace(/\.[^.]+$/, '')
      .replace(/[^\w.-]+/g, '_')
      .slice(0, 80) || 'mitra_card';

  let quality = 0.86;
  let webpBlob = null;
  while (quality >= 0.52) {
    // eslint-disable-next-line no-await-in-loop
    webpBlob = await canvasToBlob(canvas, 'image/webp', quality);
    if (webpBlob && webpBlob.size <= targetBytes) break;
    quality -= 0.08;
  }

  if (webpBlob) {
    return new File([webpBlob], `${baseName}_${outW}x${outH}.webp`, {
      type: 'image/webp',
      lastModified: Date.now(),
    });
  }

  const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', 0.8);
  if (!jpegBlob) throw new Error('Could not compress image');
  return new File([jpegBlob], `${baseName}_${outW}x${outH}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
